import { html } from 'lit'
import { property, state } from 'lit/decorators.js'
import { ClassInfo, classMap } from 'lit/directives/class-map.js'
import { unsafeSVG } from 'lit/directives/unsafe-svg.js'
import { when } from 'lit/directives/when.js'

import { icons } from '@celebration/assets/dist'

import { ClbInputBase } from '@components/input-base'
import baseStyles from '@components/input-base/styles.scss'

import { dispatchCustomEvent, bubbleEvent } from '@utils/index'
import { registerElement } from '@utils/registerElement'

import styles from './styles.scss'

@registerElement('clb-input-text')
export class ClbInputText extends ClbInputBase {
  @property({ type: String }) label = 'Label'

  @property({ type: String }) placeholder = 'Placeholder'

  @property({ type: String }) value = ''

  @property({ attribute: 'helper-text', type: String }) helperText = ''

  @property({ type: Number }) maxlength = 100

  @property({ type: String }) requiredMessage = 'obrigatório'

  @property({ type: String }) optionalMessage = 'opcional'

  @property({ type: String }) name = ''

  @property({ type: Boolean }) showMessage = false

  @property({ type: Boolean }) showHelperText = false

  @property({ type: Boolean }) required = false

  @property({ type: Boolean }) disabled = false

  @property({ type: Boolean }) error = false

  @property({ type: Boolean }) counter = false

  @state() charsCounter = 0

  static styles = [baseStyles, styles]

  static events = {
    onClbChange: 'onClbChange'
  }

  private getLabelClasses = (): ClassInfo => ({
    [`input-base-label`]: true,
    [`input-base-label--disabled`]: this.disabled,
    [`input-base-label--${super.size}`]: true
  })

  private getInputClasses = (): ClassInfo => ({
    [`input-base-input`]: true,
    [`input-base-input--${super.size}`]: true,
    [`input-base-input--error`]: this.error
  })

  private getHelperClasses = (): ClassInfo => ({
    [`input-base-helper`]: true,
    [`input-base-label--disabled`]: this.disabled,
    [`input-base-helper--error`]: this.error && this.helperText
  })

  private _bubbleEvent(evt: Event): void {
    const targetEvt = evt.target as HTMLInputElement
    this.value = targetEvt.value
    this.requestUpdate('value')

    const targetElement =
      this.shadowRoot.firstElementChild.querySelector('input')

    bubbleEvent(evt, targetElement)

    if (evt.type === 'input') {
      this.charsCounter = targetElement.value.length
      dispatchCustomEvent({
        eventName: ClbInputText.events.onClbChange,
        eventOptions: { detail: { value: targetElement?.value } },
        targetElement,
        dispatcher: this
      })
    }
  }

  render() {
    return html`
      <div class="input-base">
        <label for=${this.id} class="${classMap(this.getLabelClasses())}">
          ${this.label}
          ${when(
            this.required,
            () =>
              this.showMessage
                ? html`
                    <span class="input-base-label--message"
                      >(${this.requiredMessage})</span
                    >
                  `
                : null,
            () =>
              this.showMessage
                ? html`
                    <span class="input-base-label--message"
                      >(${this.optionalMessage})</span
                    >
                  `
                : null
          )}
        </label>
        <input
          id="${this.id}"
          type="${this.type}"
          class="${classMap(this.getInputClasses())}"
          placeholder="${this.placeholder}"
          .value=${this.value}
          maxlength=${this.maxlength}
          aria-label="${this.label}"
          name="${this.name}"
          ?disabled=${this.disabled}
          ?required=${this.required}
          ?counter=${this.counter}
          @focus=${this._bubbleEvent}
          @blur=${this._bubbleEvent}
          @input=${this._bubbleEvent}
        />
          ${when(
            this.showHelperText,
            () => html`
              <span class="${classMap(this.getHelperClasses())}">
                <span>${unsafeSVG(icons['Spam'])}</span>
                ${this.helperText}
              </span>
            `,
            () => null
          )}
          ${
            this.counter
              ? html`
                  <span id="textarea-counter"
                    >${this.charsCounter}/${this.maxlength}</span
                  >
                `
              : html``
          }
        </span>
      </div>
    `
  }
}
